// src/data/officeSuccession.js
//
// G8.4 / Track 2, WO-001, phase S1 (2026-09-14, task list section 225; ruling 96 - built here, not
// routed). Design of record: data\YCP_OFFICE_SUCCESSION_DESIGN_2026-08-17.md SS3 with amendments A1-A3.
//
//   Offices are durable. People occupy them for terms. Nothing is ever overwritten.
//
//   offices/{officeId}      the seat: GO-342, LC1951-342A ...  type, craft, railroad, territory,
//                           parentOffice (an LC's GC), status when no term is open ('vacant' with a
//                           reason, or 'not_of_record' under ruling 54), provenance.
//   officeTerms/{termId}    append-only, one document per occupancy: officeId, holderUid, holderEmail,
//                           holderName, startedOn (null = not on record), endedOn (null = current),
//                           method, installedBy, installedAt, attestedBy[], endReason, evidence, notes.
//
// This module is PURE. It reads no database and writes none: the screens pass it the term documents
// they hold, and the seed script and (later, S2) the setOfficeHolder function pass it what they are
// about to write so it can refuse the wrong thing. That is the shape of every module in src\data
// and it is what lets TST-1 prove it without an emulator.
//
// What is NOT here, by design: lame-duck status, 'vacant' as a term status, a term id built from a
// date. The second surface's draft of 2026-09-12 (Claude outputs\officeSuccession.js) carried all
// three and its holderOn returned null for every OPEN term (endDate null compares as the epoch).
// A vacancy is a state of the OFFICE between terms, never a term; the constitution's interim
// provisions (A2.1, A2.2) are a term with method 'interim'.

export const OFFICE_TYPES = ['general_committee', 'local_committee'];
export const METHODS = ['elected', 'appointed', 'interim', 'inherited_vacancy'];
export const END_REASONS = ['term_end', 'resignation', 'retirement', 'removal', 'death', 'incapacity', 'left_craft'];
export const OFFICE_STATUS = ['held', 'vacant', 'not_of_record'];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isDate = (s) => typeof s === 'string' && DATE_RE.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z'));
const day = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);   // Firestore Timestamp
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return null;
};

// Was this term in force on `date`? An open term (endedOn null) is in force from its start onward;
// a term whose start is not on record (startedOn null) is in force from before the record began.
export function termInForceOn(term, date) {
  const d = day(date);
  if (!term || !d) return false;
  const s = day(term.startedOn);
  const e = day(term.endedOn);
  if (s && d < s) return false;
  if (e && d > e) return false;
  return true;
}

// The term record in force on `date` for one office, or null. The whole point of the design (SS3).
// With overlapping records (which validateTerm refuses at write time) the latest start wins, so a
// corrupt history still answers deterministically rather than by array order.
export function holderOn(terms, officeId, date) {
  const inForce = (terms || []).filter(t => t && t.officeId === officeId && termInForceOn(t, date));
  if (!inForce.length) return null;
  inForce.sort((a, b) => String(day(b.startedOn) || '0000') < String(day(a.startedOn) || '0000') ? -1 : 1);
  return inForce[0];
}

export function currentHolder(terms, officeId, today) {
  return holderOn(terms, officeId, today || new Date());
}

// The state of an office on a date: held (with the term), vacant (with the office's recorded reason),
// or not_of_record (ruling 54 - the seat exists, its holder is not carried before launch).
export function officeStateOn(office, terms, date) {
  const term = office ? holderOn(terms, office.officeId, date) : null;
  if (term) return { state: 'held', term, acting: term.method === 'interim' };
  const status = office && office.status === 'vacant' ? 'vacant' : 'not_of_record';
  return { state: status, term: null, acting: false, reason: (office && office.statusReason) || null };
}

// All terms of one office, oldest first - the succession chain.
export function officeHistory(terms, officeId) {
  return (terms || []).filter(t => t && t.officeId === officeId)
    .sort((a, b) => String(day(a.startedOn) || '0000') < String(day(b.startedOn) || '0000') ? -1 : 1);
}

// Refuses what the design's SS5 forbids before anything writes it.
//   1. No self-grant: installedBy is never the holder.
//   2. One holder per office at a time (A3.3 point 3, withdrawn 08-21, ratified 08-23): a new term
//      may not overlap any term on record for the office.
//   3. Dates are dates; endedOn is never before startedOn; a closed term carries its endReason.
//   4. The office must exist and the method must be one the constitution knows.
export function validateTerm(term, offices, existingTerms) {
  const errors = [];
  if (!term || typeof term !== 'object') return { ok: false, errors: ['no term'] };
  const office = (offices || []).find(o => o && o.officeId === term.officeId);
  if (!office) errors.push('office ' + term.officeId + ' is not in the offices registry');
  if (!term.holderUid && !term.holderEmail) errors.push('a term names its holder by uid or e-mail');
  if (!term.holderName) errors.push('a term names its holder');
  if (term.method != null && !METHODS.includes(term.method)) errors.push('method ' + term.method + ' is not one the constitution knows (' + METHODS.join(', ') + ')');
  if (term.startedOn != null && !isDate(term.startedOn)) errors.push('startedOn must be YYYY-MM-DD or null (not on record)');
  if (term.endedOn != null && !isDate(term.endedOn)) errors.push('endedOn must be YYYY-MM-DD or null (current)');
  if (isDate(term.startedOn) && isDate(term.endedOn) && term.endedOn < term.startedOn) errors.push('a term cannot end before it starts');
  if (term.endedOn && !END_REASONS.includes(term.endReason)) errors.push('a closed term carries its endReason (' + END_REASONS.join(', ') + ')');
  if (term.installedBy && term.holderUid && term.installedBy === term.holderUid) errors.push('no self-grant: the installer cannot be the holder (design SS5.1)');
  for (const other of (existingTerms || [])) {
    if (!other || other.officeId !== term.officeId || (term.termId && other.termId === term.termId)) continue;
    if (termsOverlap(term, other)) errors.push('overlaps term ' + (other.termId || '(unnamed)') + ' held by ' + other.holderName + ' - one holder per office at a time');
  }
  return { ok: errors.length === 0, errors };
}

export function termsOverlap(a, b) {
  const as = day(a.startedOn) || '0000-00-00', ae = day(a.endedOn) || '9999-99-99';
  const bs = day(b.startedOn) || '0000-00-00', be = day(b.endedOn) || '9999-99-99';
  return as <= be && bs <= ae;
}

// The patch that CLOSES a term. Nothing is deleted; an ending is written (SS5.3).
export function closeTermPatch(term, endedOn, endReason, closedBy) {
  const errors = [];
  if (!term) errors.push('no term');
  if (term && term.endedOn) errors.push('term already ended ' + day(term.endedOn));
  if (!isDate(endedOn)) errors.push('endedOn must be YYYY-MM-DD');
  if (!END_REASONS.includes(endReason)) errors.push('endReason must be one of ' + END_REASONS.join(', '));
  if (term && isDate(term.startedOn) && isDate(endedOn) && endedOn < term.startedOn) errors.push('a term cannot end before it starts');
  if (!closedBy) errors.push('the act is attributed (SS5.4)');
  if (errors.length) return { ok: false, errors };
  return { ok: true, patch: { endedOn, endReason, closedBy, closedAt: new Date().toISOString() } };
}

// Wording for a screen. Never invents a date it does not have.
export function holderLine(office, terms, date) {
  const st = officeStateOn(office, terms, date);
  const label = office ? (office.label || office.officeId) : 'this office';
  if (st.state === 'held') {
    const t = st.term;
    const since = day(t.startedOn) ? 'since ' + day(t.startedOn) : 'start of term not on record';
    return (st.acting ? 'Acting ' : '') + label + ': ' + t.holderName + ' (' + since + (day(t.endedOn) ? ', ended ' + day(t.endedOn) : '') + ')';
  }
  if (st.state === 'vacant') return label + ': VACANT' + (st.reason ? ' - ' + st.reason : '');
  return label + ': holder not of record on this platform';
}

// ---- S2: the one writer of a term (2026-09-14, task list section 236) ------------------------------
//
// setOfficeHolder (functions\index.js) is the only thing that opens or closes a term and the only
// thing that writes role / committee / scopes claims (design SS5.2). It executes a PLAN this pure
// function returns, so the whole of the decision - who may, what closes, what opens, what each
// person's claims become - is proved here without Firestore, and the function only carries it out.
// scripts\set-roles.js is break-glass from this cycle: kept, documented, not the operational path.

const DAY_MS = 24 * 60 * 60 * 1000;
function dayBefore(iso) { return new Date(Date.parse(iso + 'T00:00:00Z') - DAY_MS).toISOString().slice(0, 10); }
function pad4(n) { return String(n).padStart(4, '0'); }

// A person's claims are a PROJECTION of the terms he holds today (SS3: custom claims are a cache of
// the current term, written by one function and by nothing else). An admin account is never
// touched by this - admin is granted by scripts\set-roles.js break-glass and by nothing else.
export function claimsProjection(openTerms, offices) {
  const held = (openTerms || []).filter(t => t && !t.endedOn).map(t => (offices || []).find(o => o && o.officeId === t.officeId)).filter(Boolean);
  const gc = held.filter(o => o.type === 'general_committee');
  const lc = held.filter(o => o.type === 'local_committee');
  if (gc.length > 1) return { ok: false, errors: ['one person holds two General Chairperson offices (' + gc.map(o => o.officeId).join(', ') + ') - the constitution does not contemplate it and this projection refuses it'] };
  if (gc.length === 1) {
    if (lc.length) return { ok: false, errors: ['a General Chairperson cannot also hold a Local Chairperson seat of the same committee (SS82 lines 122-125): ' + lc.map(o => o.officeId).join(', ')] };
    return { ok: true, errors: [], claims: { role: 'general_chair', committee: gc[0].officeId, scopes: null } };
  }
  if (lc.length) {
    const committees = Array.from(new Set(lc.map(o => o.parentOffice)));
    if (committees.length > 1) return { ok: false, errors: ['Local Chairperson seats in two committees (' + committees.join(', ') + ') cannot share one token'] };
    return { ok: true, errors: [], claims: { role: 'local_chair', committee: committees[0], scopes: lc.map(o => o.officeId).sort() } };
  }
  return { ok: true, errors: [], claims: { role: null, committee: null, scopes: null } };   // holds nothing: the elevated claims come off
}

// The plan. Refuses before it plans; returns everything the function must write, and nothing else.
export function planSetOfficeHolder(input) {
  const i = input || {};
  const errors = [];
  const office = i.office;
  const offices = i.offices || (office ? [office] : []);
  const terms = Array.isArray(i.terms) ? i.terms : [];
  if (!office || !office.officeId) errors.push('office not on record');
  if (!i.callerUid) errors.push('no caller');
  if (!i.holder || !i.holder.uid) errors.push('the holder must exist as an account (uid) - a term is by account');
  if (i.holder && i.holder.uid && i.holder.uid === i.callerUid) errors.push('no self-grant: the installer cannot be the holder (design SS5.1)');
  if (!isDate(i.startedOn)) errors.push('startedOn must be YYYY-MM-DD - the day the term begins is a fact, not a default');
  if (!METHODS.includes(i.method)) errors.push('method must be one of ' + METHODS.join(', '));
  const endReason = i.endReason || 'term_end';
  if (!END_REASONS.includes(endReason)) errors.push('endReason for the closing term must be one of ' + END_REASONS.join(', '));
  if (i.holderIsAdmin) errors.push('an admin account is not seated in an office through this path');
  if (errors.length) return { ok: false, errors };

  const history = officeHistory(terms, office.officeId);
  const open = history.filter(t => !t.endedOn);
  if (open.length > 1) return { ok: false, errors: ['the office carries ' + open.length + ' open terms - the record is corrupt and must be repaired by hand before a new holder is set'] };
  const prev = open[0] || null;
  if (prev && prev.holderUid === i.holder.uid) return { ok: false, errors: [i.holder.name + ' already holds ' + office.officeId + ' (term ' + prev.termId + ')'] };

  let closing = null;
  if (prev) {
    const endedOn = dayBefore(i.startedOn);
    const c = closeTermPatch(prev, endedOn, endReason, i.callerUid);
    if (!c.ok) return { ok: false, errors: c.errors.map(e => 'closing ' + prev.termId + ': ' + e) };
    closing = { termId: prev.termId, patch: c.patch };
  }
  const termId = office.officeId + '#' + pad4(history.length + 1);
  const newTerm = {
    termId, officeId: office.officeId,
    holderUid: i.holder.uid, holderEmail: i.holder.email || null, holderName: i.holder.name || i.holder.email || 'Unknown',
    startedOn: i.startedOn, endedOn: null, method: i.method, endReason: null,
    installedBy: i.callerUid, installedAt: i.at || new Date().toISOString(),
    attestedBy: Array.isArray(i.attestedBy) ? i.attestedBy.filter(Boolean) : [],
    evidence: String(i.evidence || '').trim() || null, notes: String(i.notes || '').trim() || null,
  };
  const after = terms.map(t => (closing && t.termId === closing.termId) ? { ...t, ...closing.patch } : t);
  const v = validateTerm(newTerm, offices, after);
  if (!v.ok) return { ok: false, errors: v.errors };

  // Claims for both people, projected from what each holds once this lands.
  const holderOpen = (i.holderOpenTerms || []).filter(t => t && !t.endedOn && t.officeId !== office.officeId).concat([newTerm]);
  const holderClaims = claimsProjection(holderOpen, offices);
  if (!holderClaims.ok) return { ok: false, errors: holderClaims.errors };
  let prevClaims = null;
  if (prev) {
    const prevOpen = (i.prevOpenTerms || []).filter(t => t && !t.endedOn && t.termId !== prev.termId);
    const p = claimsProjection(prevOpen, offices);
    if (!p.ok) return { ok: false, errors: p.errors.map(e => 'outgoing holder: ' + e) };
    prevClaims = { uid: prev.holderUid, claims: p.claims, isAdmin: !!i.prevIsAdmin };
  }
  return {
    ok: true, errors: [],
    closing, newTerm,
    holderClaims: { uid: i.holder.uid, claims: holderClaims.claims },
    prevClaims,
    audit: { action: 'set_office_holder', officeId: office.officeId, from: prev ? { uid: prev.holderUid, name: prev.holderName, termId: prev.termId } : null, to: { uid: i.holder.uid, name: newTerm.holderName, termId }, actor: i.callerUid, at: newTerm.installedAt, method: i.method, endReason: prev ? endReason : null },
  };
}

// ---- S4a: who may seat whom (2026-09-14, task list section 241) ------------------------------------
//
// Design A3.5 S4a: "GC attests his committee's Local Chairs; roster rows return V one at a time."
// The platform administrator may seat any office; a General Chairperson may seat the LOCAL
// Chairperson seats of his own committee and nothing else - never a General Chairperson's office,
// never another committee's seat. The rule lives here so the function and the screen share it.
export function mayAttestSeat(caller, office) {
  if (!caller || !office) return { ok: false, reason: 'no caller or office' };
  if (caller.isAdmin || caller.role === 'admin') return { ok: true, as: 'admin' };
  if (caller.role === 'general_chair' && office.type === 'local_committee' && office.parentOffice && office.parentOffice === caller.committee) return { ok: true, as: 'general_chair' };
  if (caller.role === 'general_chair' && office.type === 'general_committee') return { ok: false, reason: 'a General Chairperson does not seat a General Chairperson - that is the two-party handover (S6) or the administrator' };
  if (caller.role === 'general_chair') return { ok: false, reason: office.officeId + ' is not a seat of ' + (caller.committee || 'your committee') };
  return { ok: false, reason: 'only the platform administrator or the committee\'s General Chairperson seats an office holder' };
}

// The attestation line a GC's act writes onto the term (design S4a: the roster row returns V).
export function attestationLine(caller, at) {
  const who = (caller && (caller.name || caller.email)) || 'unknown';
  const role = caller && caller.isAdmin ? 'platform administrator' : caller && caller.role === 'general_chair' ? 'General Chairperson ' + (caller.committee || '') : (caller && caller.role) || 'unknown';
  return who + ' (' + role.trim() + ') ' + String(at || new Date().toISOString()).slice(0, 10);
}

// ---- S5: offboarding - the plan that closes a term (2026-09-14, task list section 242) ---------------
//
// Design A3.5 S5: "Offboarding: close term, revoke, drafts, vacant state, notify". Who may: whoever may
// seat the office (mayAttestSeat - the administrator, or the GC for his committee's LC seats), and the
// HOLDER HIMSELF for his own term (a resignation is his act to record). The plan returns the closing
// patch, the office's new state (VACANT with the reason - a vacancy is a state of the office), the
// outgoing holder's projected claims (what he still holds, or nothing), and the audit row. What it
// cannot do is decide what happens to his drafts: it counts them for the caller (design SS6 names the
// orphan case as undefined); reassigning them is a later act with its own rule.
export function mayCloseTerm(caller, office, term) {
  if (!caller || !office || !term) return { ok: false, reason: 'no caller, office or term' };
  if (term.endedOn) return { ok: false, reason: 'term ' + (term.termId || '') + ' already ended ' + String(term.endedOn).slice(0, 10) };
  if (caller.uid && term.holderUid === caller.uid) return { ok: true, as: 'holder' };
  const m = mayAttestSeat(caller, office);
  return m.ok ? m : { ok: false, reason: m.reason };
}

export function planCloseOfficeTerm(input) {
  const i = input || {};
  const errors = [];
  const office = i.office; const offices = i.offices || (office ? [office] : []);
  const terms = Array.isArray(i.terms) ? i.terms : [];
  if (!office || !office.officeId) errors.push('office not on record');
  if (!i.callerUid) errors.push('no caller');
  if (!isDate(i.endedOn)) errors.push('endedOn must be YYYY-MM-DD - the day the term ends is a fact');
  if (!END_REASONS.includes(i.endReason)) errors.push('endReason must be one of ' + END_REASONS.join(', '));
  if (errors.length) return { ok: false, errors };
  const open = officeHistory(terms, office.officeId).filter(t => !t.endedOn);
  if (!open.length) return { ok: false, errors: [office.officeId + ' has no open term to close - it is already ' + (office.status === 'vacant' ? 'VACANT' : 'without a holder of record')] };
  if (open.length > 1) return { ok: false, errors: ['the office carries ' + open.length + ' open terms - repaired by hand before anything closes'] };
  const term = open[0];
  const may = mayCloseTerm(i.caller || { uid: i.callerUid }, office, term);
  if (!may.ok) return { ok: false, errors: [may.reason] };
  const c = closeTermPatch(term, i.endedOn, i.endReason, i.callerUid);
  if (!c.ok) return { ok: false, errors: c.errors };
  const reason = i.endReason.replace('_', ' ') + (term.holderName ? ' of ' + term.holderName : '') + ' ' + i.endedOn;
  const holderOpen = (i.holderOpenTerms || []).filter(t => t && !t.endedOn && t.termId !== term.termId);
  const proj = claimsProjection(holderOpen, offices);
  if (!proj.ok) return { ok: false, errors: proj.errors.map(e => 'outgoing holder: ' + e) };
  return {
    ok: true, errors: [],
    closing: { termId: term.termId, patch: { ...c.patch, closeNote: String(i.note || '').trim() || null } },
    officePatch: { status: 'vacant', statusReason: reason, vacantSince: i.endedOn },
    holderClaims: { uid: term.holderUid, claims: proj.claims, isAdmin: !!i.holderIsAdmin },
    draftsOrphaned: typeof i.draftCount === 'number' ? i.draftCount : null,
    audit: { action: 'close_office_term', officeId: office.officeId, termId: term.termId, holder: { uid: term.holderUid, name: term.holderName }, endedOn: i.endedOn, endReason: i.endReason, actor: i.callerUid, as: may.as, at: c.patch.closedAt },
  };
}

// ---- S6: the planned handover, two-party (2026-09-14, task list section 242) ------------------------
//
// Design SS4 "Handover (planned) - two-party, neither side alone": the outgoing holder proposes
// (successor's account, effective date, method); the successor sees it and ACCEPTS; on accept one
// act closes term A with term_end, opens term B, and rewrites both people's claims. Two clicks each;
// neither can complete it alone and neither can do it silently. handovers/{id} holds the proposal;
// only the functions write it.
export const HANDOVER_STATES = ['proposed', 'accepted', 'declined', 'withdrawn'];

export function planProposeHandover(input) {
  const i = input || {};
  const errors = [];
  const office = i.office; const terms = Array.isArray(i.terms) ? i.terms : [];
  if (!office || !office.officeId) errors.push('office not on record');
  if (!i.caller || !i.caller.uid) errors.push('no caller');
  if (!i.successor || !i.successor.uid) errors.push('the successor must exist as an account');
  if (i.caller && i.successor && i.caller.uid === i.successor.uid) errors.push('a holder cannot hand an office to himself');
  if (!isDate(i.effectiveOn)) errors.push('effectiveOn must be YYYY-MM-DD - the day the successor takes up the office');
  if (!['elected', 'appointed'].includes(i.method)) errors.push('a planned handover is elected or appointed; interim and inherited_vacancy are the vacancy flow (S7)');
  if (i.successorIsAdmin) errors.push('an admin account is not seated in an office through this path');
  if (errors.length) return { ok: false, errors };
  const open = officeHistory(terms, office.officeId).filter(t => !t.endedOn);
  if (open.length !== 1) return { ok: false, errors: [open.length ? 'the office carries ' + open.length + ' open terms' : office.officeId + ' has no sitting holder - a vacant office is filled by the administrator or the vacancy flow (S7), not handed off'] };
  const sitting = open[0];
  if (!(i.caller.isAdmin || sitting.holderUid === i.caller.uid)) return { ok: false, errors: ['only the sitting holder of ' + office.officeId + ' (or the administrator) proposes its handover'] };
  if (isDate(sitting.startedOn) && i.effectiveOn <= sitting.startedOn) return { ok: false, errors: ['the handover cannot take effect on or before the day the sitting term began'] };
  if ((i.pending || []).some(h => h && h.officeId === office.officeId && h.status === 'proposed')) return { ok: false, errors: ['a handover of ' + office.officeId + ' is already proposed - withdraw it first'] };
  return {
    ok: true, errors: [],
    record: {
      officeId: office.officeId, committee: office.type === 'general_committee' ? office.officeId : (office.parentOffice || null), officeType: office.type,
      termIdOpen: sitting.termId,
      proposerUid: i.caller.uid, proposerName: i.caller.name || i.caller.email || 'Unknown', proposedAs: i.caller.isAdmin && sitting.holderUid !== i.caller.uid ? 'admin' : 'holder',
      successorUid: i.successor.uid, successorEmail: i.successor.email || null, successorName: i.successor.name || i.successor.email || 'Unknown',
      effectiveOn: i.effectiveOn, method: i.method, note: String(i.note || '').trim() || null,
      status: 'proposed', proposedAt: i.at || new Date().toISOString(), decidedAt: null, decidedBy: null, resultTermId: null,
      // RULING 304 (Bob 2026-10-09, audit F40 (b)): a GENERAL CHAIRPERSON's office does not change hands until the
      // administrator has acknowledged it. A handover the administrator proposes himself is acknowledged by that act.
      adminAcknowledgedAt: (office.type === 'general_committee' && i.caller.isAdmin) ? (i.at || new Date().toISOString()) : null,
      adminAcknowledgedBy: (office.type === 'general_committee' && i.caller.isAdmin) ? i.caller.uid : null,
    },
  };
}

// Ruling 304: does this handover still wait on the administrator? Only a General Chairperson's office does.
export function handoverAwaitsAdmin(handover) {
  return !!handover && handover.officeType === 'general_committee' && !handover.adminAcknowledgedAt;
}

// Accept: the successor's act. Returns the setOfficeHolder plan to execute, with the OUTGOING holder
// as installer (so no self-grant is possible) and both men on attestedBy.
export function planAcceptHandover(input) {
  const i = input || {};
  const h = i.handover;
  if (!h) return { ok: false, errors: ['no handover'] };
  if (h.status !== 'proposed') return { ok: false, errors: ['this handover is ' + h.status + ', not open'] };
  if (!i.callerUid || i.callerUid !== h.successorUid) return { ok: false, errors: ['only the named successor accepts a handover'] };
  if (handoverAwaitsAdmin(h)) return { ok: false, errors: ['a General Chairperson\'s office changes hands only after the administrator has acknowledged the handover - it is waiting on him'] };
  const plan = planSetOfficeHolder({
    office: i.office, offices: i.offices, terms: i.terms,
    callerUid: h.proposerUid,
    holder: { uid: h.successorUid, email: h.successorEmail, name: h.successorName },
    holderIsAdmin: !!i.successorIsAdmin, holderOpenTerms: i.holderOpenTerms, prevOpenTerms: i.prevOpenTerms, prevIsAdmin: !!i.prevIsAdmin,
    startedOn: h.effectiveOn, method: h.method, endReason: 'term_end',
    attestedBy: [String(h.proposerName) + ' (outgoing) proposed ' + String(h.proposedAt).slice(0, 10)]
      .concat(h.adminAcknowledgedAt ? ['the administrator acknowledged ' + String(h.adminAcknowledgedAt).slice(0, 10)] : [])
      .concat([String(h.successorName) + ' (successor) accepted ' + String(i.at || new Date().toISOString()).slice(0, 10)]),
    evidence: 'Two-party handover ' + (h.id || '') + ' (design SS4)', notes: h.note, at: i.at,
  });
  if (!plan.ok) return plan;
  if (!plan.closing || plan.closing.termId !== h.termIdOpen) return { ok: false, errors: ['the sitting term changed since the handover was proposed (' + h.termIdOpen + ') - propose it again'] };
  return { ...plan, handoverPatch: { status: 'accepted', decidedAt: i.at || new Date().toISOString(), decidedBy: i.callerUid, resultTermId: plan.newTerm.termId } };
}

export function planDecideHandover(handover, callerUid, decision, at, callerIsAdmin) {
  if (!handover) return { ok: false, errors: ['no handover'] };
  if (handover.status !== 'proposed') return { ok: false, errors: ['this handover is ' + handover.status + ', not open'] };
  // Ruling 304: the administrator's acknowledgement of a General Chairperson handover. It records who and when and leaves
  // the handover open - the successor still accepts (or declines) it himself.
  if (decision === 'acknowledged') {
    if (!callerIsAdmin) return { ok: false, errors: ['only the administrator acknowledges a handover'] };
    if (handover.officeType !== 'general_committee') return { ok: false, errors: ['only a General Chairperson\'s office needs the administrator\'s acknowledgement'] };
    if (handover.adminAcknowledgedAt) return { ok: false, errors: ['this handover was already acknowledged ' + String(handover.adminAcknowledgedAt).slice(0, 10)] };
    const t = at || new Date().toISOString();
    return { ok: true, errors: [], patch: { adminAcknowledgedAt: t, adminAcknowledgedBy: callerUid } };
  }
  if (decision === 'declined' && callerUid !== handover.successorUid) return { ok: false, errors: ['only the named successor declines'] };
  if (decision === 'withdrawn' && callerUid !== handover.proposerUid) return { ok: false, errors: ['only the proposer withdraws'] };
  if (!['declined', 'withdrawn'].includes(decision)) return { ok: false, errors: ['decision must be declined or withdrawn'] };
  return { ok: true, errors: [], patch: { status: decision, decidedAt: at || new Date().toISOString(), decidedBy: callerUid } };
}

// ---- S7: vacancy and interim - the vice steps up (2026-09-14, task list section 245) ----------------
//
// Design A2.1 / A2.2 / A3.5 S7, from the constitution as read: SS82 lines 132-138 - "In the interim the
// Vice General Chairperson designated by the Committee at the time of their election will fill the
// vacancy", and the Secretary of the General Committee "shall promptly notify the President
// Transportation Division"; SS81 lines 32-40 - the Vice Chairperson of the Local Committee "shall act as
// Chairperson ... until the office is filled as provided in Section 57", and where there is more than
// one, "the Local Committee shall designate" which. Bob, 2026-08-17: "the vice steps up at both
// levels ... deadlines do not pause for a vacancy."
//
// Three facts the platform therefore carries, and carries on the OFFICE (they belong to the office,
// not to a term): `designatedVice` - the person the committee designated, in advance, not in the
// crisis; `secretary` (general_committee offices only) - the constitutional notifier; and, once a
// vacancy has been closed onto the office by S5, the interim term the vice holds.
//
// THE INTERIM TERM IS A TERM (SS4 of the design): method 'interim', the office says ACTING everywhere,
// and it carries `expectedEndOn` - the election the vacancy is filled by - so that a temporary
// authority which runs past its date raises a flag instead of quietly becoming permanent. WHO
// INSTALLS IT: the vice cannot install himself (no self-grant, SS5.1) and the chairman is gone. The
// authority is the DESIGNATION, recorded earlier by someone else; so the plan names the person who
// recorded the designation as installer, cites the constitution as evidence, and records the vice's
// own acceptance on attestedBy. Nothing is granted that the constitution did not already grant.
export const DESIGNATIONS = ['designatedVice', 'secretary'];
export const VACANCY_NOTICE_KINDS = { STEP_UP: 'office_vacant_step_up', PTD_NOTICE: 'gc_vacancy_ptd_notice', NO_VICE: 'office_vacant_no_vice' };
export const NOTICE_AUDIENCE_OFFICE_HOLDER = 'office_holder';

// Who may record a designation: the administrator; the sitting holder of the office (the committee's
// act is recorded by its chairman); and, for a Local Chairperson seat, the committee's GC (mayAttestSeat).
export function mayDesignate(caller, office, terms) {
  if (!caller || !office) return { ok: false, reason: 'no caller or office' };
  if (caller.isAdmin || caller.role === 'admin') return { ok: true, as: 'admin' };
  const sitting = (terms || []).find(t => t && t.officeId === office.officeId && !t.endedOn) || null;
  if (sitting && caller.uid && sitting.holderUid === caller.uid) return { ok: true, as: 'holder' };
  const m = mayAttestSeat(caller, office);
  if (m.ok) return m;
  return { ok: false, reason: 'a designation is recorded by the office\'s sitting holder, the committee\'s General Chairperson (for a Local seat) or the administrator' };
}

export function planDesignateOfficer(input) {
  const i = input || {};
  const errors = [];
  const office = i.office; const terms = Array.isArray(i.terms) ? i.terms : [];
  if (!office || !office.officeId) errors.push('office not on record');
  if (!i.caller || !i.caller.uid) errors.push('no caller');
  if (!DESIGNATIONS.includes(i.kind)) errors.push('kind must be one of ' + DESIGNATIONS.join(', '));
  if (i.kind === 'secretary' && office && office.type !== 'general_committee') errors.push('a Secretary is an officer of the General Committee (SS82); a Local Committee seat carries none here');
  if (!i.person || !i.person.uid) errors.push('the designated person must exist as an account (uid)');
  if (i.personIsAdmin) errors.push('an admin account is not designated to an office');
  if (errors.length) return { ok: false, errors };
  const may = mayDesignate(i.caller, office, terms);
  if (!may.ok) return { ok: false, errors: [may.reason] };
  const sitting = terms.find(t => t && t.officeId === office.officeId && !t.endedOn) || null;
  if (i.kind === 'designatedVice' && sitting && sitting.holderUid === i.person.uid) return { ok: false, errors: [i.person.name || i.person.uid + ' holds ' + office.officeId + ' now - the vice is someone else'] };
  const at = i.at || new Date().toISOString();
  const record = {
    uid: i.person.uid, email: i.person.email || null, name: i.person.name || i.person.email || 'Unknown',
    designatedOn: isDate(i.designatedOn) ? i.designatedOn : at.slice(0, 10),
    designatedBy: i.caller.uid, designatedByName: i.caller.name || i.caller.email || 'Unknown', designatedAs: may.as,
    basis: i.kind === 'secretary' ? 'SS82 - Secretary of the General Committee' : (office.type === 'general_committee' ? 'SS82 lines 132-138 - designated by the Committee at the time of their election' : 'SS81 lines 32-40 - designated by the Local Committee'),
    note: String(i.note || '').trim() || null, recordedAt: at,
  };
  return {
    ok: true, errors: [],
    officePatch: { [i.kind]: record },
    audit: { action: 'designate_officer', officeId: office.officeId, kind: i.kind, person: { uid: record.uid, name: record.name }, actor: i.caller.uid, as: may.as, at },
  };
}

// The notices a vacancy raises, as notification records for the function to write (the module writes
// nothing - src\data\notifications.js's rule). Addressed to PEOPLE by uid (audience 'office_holder'):
// the designated vice is told he acts from today; at GC level the Secretary is told the PTD must be
// notified promptly - and if there is no Secretary of record, the administrator is told THAT, because
// a constitutional duty with nobody to carry it is the thing the record must not hide.
export function vacancyNotices(office, closing, at) {
  const o = office || {}; const c = closing || {};
  const when = at || new Date().toISOString();
  const committee = o.type === 'general_committee' ? o.officeId : (o.parentOffice || '');
  const label = o.label || o.officeId || 'the office';
  const base = { channel: 'in_app', claimId: '', committee, scopeKey: o.type === 'local_committee' ? o.officeId : '', officeId: o.officeId || null, actorName: c.actorName || 'the platform', actorRole: c.actorRole || 'system', createdAt: when, readAt: null };
  const out = [];
  if (o.designatedVice && o.designatedVice.uid) {
    out.push({ ...base, kind: VACANCY_NOTICE_KINDS.STEP_UP, audience: NOTICE_AUDIENCE_OFFICE_HOLDER, recipientUid: o.designatedVice.uid,
      title: 'You act as ' + (o.type === 'general_committee' ? 'General Chairperson' : 'Local Chairperson') + ' from ' + (c.endedOn || when.slice(0, 10)),
      body: label + ' is vacant (' + (c.endReason || 'vacancy').replace('_', ' ') + (c.holderName ? ' of ' + c.holderName : '') + '). You are its designated ' + (o.type === 'general_committee' ? 'Vice General Chairperson' : 'Vice Chairperson') + ' and the constitution seats you in the interim (' + (o.type === 'general_committee' ? 'SS82 lines 132-138' : 'SS81 lines 32-40') + '). Deadlines on its claims do not pause. Open Seats of record and take up the office with the date of the election that will fill it.' });
  } else {
    out.push({ ...base, kind: VACANCY_NOTICE_KINDS.NO_VICE, audience: NOTICE_AUDIENCE_OFFICE_HOLDER, recipientUid: null,
      title: label + ' is vacant and has NO designated vice of record',
      body: 'The constitution seats the designated vice in the interim, and none is recorded on this office. Its claims have no acting holder on the platform until one is designated (Seats of record) or a successor is seated.' });
  }
  if (o.type === 'general_committee') {
    if (o.secretary && o.secretary.uid) {
      out.push({ ...base, kind: VACANCY_NOTICE_KINDS.PTD_NOTICE, audience: NOTICE_AUDIENCE_OFFICE_HOLDER, recipientUid: o.secretary.uid,
        title: 'Notify the President Transportation Division: ' + label + ' is vacant',
        body: 'SS82 lines 132-138: "The Secretary of the General Committee shall promptly notify the President Transportation Division in the event of a vacancy in the office of Chairperson. The President Transportation Division shall order an election." You are the Secretary of record. Record the date you gave notice on the office.' });
    } else {
      out.push({ ...base, kind: VACANCY_NOTICE_KINDS.PTD_NOTICE, audience: NOTICE_AUDIENCE_OFFICE_HOLDER, recipientUid: null,
        title: label + ' is vacant and has NO Secretary of record to notify the PTD',
        body: 'SS82 lines 132-138 require the Secretary of the General Committee to notify the President Transportation Division promptly. No Secretary is recorded on this office; the duty stands and nobody on the platform is carrying it.' });
    }
  }
  return out;
}

// The vice steps up. Returns a setOfficeHolder plan (method 'interim', expectedEndOn on the term).
export function planStepUpInterim(input) {
  const i = input || {};
  const errors = [];
  const office = i.office; const offices = i.offices || (office ? [office] : []);
  const terms = Array.isArray(i.terms) ? i.terms : [];
  if (!office || !office.officeId) errors.push('office not on record');
  if (!i.caller || !i.caller.uid) errors.push('no caller');
  if (office && !(office.designatedVice && office.designatedVice.uid)) errors.push((office.officeId || 'the office') + ' has no designated vice of record - the constitution seats the DESIGNATED vice, so one is recorded first (or a successor is seated outright)');
  if (!isDate(i.startedOn)) errors.push('startedOn must be YYYY-MM-DD - the day the vice begins to act');
  if (!isDate(i.expectedEndOn)) errors.push('expectedEndOn must be YYYY-MM-DD - the election that fills the vacancy (SS57 / the PTD\'s order); an interim term without a date is how temporary authority becomes permanent');
  if (isDate(i.startedOn) && isDate(i.expectedEndOn) && i.expectedEndOn <= i.startedOn) errors.push('expectedEndOn must be after startedOn');
  if (errors.length) return { ok: false, errors };
  const vice = office.designatedVice;
  const open = officeHistory(terms, office.officeId).filter(t => !t.endedOn);
  if (open.length) return { ok: false, errors: [office.officeId + ' is held by ' + open[0].holderName + ' (term ' + open[0].termId + ') - a vice steps up into a VACANT office; close the sitting term first (S5)'] };
  if (isDate(office.vacantSince) && i.startedOn < office.vacantSince) return { ok: false, errors: ['the vice cannot act from before the vacancy (' + office.vacantSince + ')'] };
  const callerIsVice = i.caller.uid === vice.uid;
  // Ruling 304 (audit F40 (b)): a General Chairperson's office is taken up in the interim only on the administrator's
  // act - the vice cannot take it up himself, because the chairman who designated him could have named anyone.
  if (office.type === 'general_committee' && !(i.caller.isAdmin || i.caller.role === 'admin')) {
    return { ok: false, errors: ['a General Chairperson\'s office is taken up in the interim only when the administrator records it - ask him to record the step-up'] };
  }
  if (!callerIsVice) {
    const may = mayAttestSeat(i.caller, office);
    if (!may.ok) return { ok: false, errors: ['the designated vice takes up the office himself, or the administrator / the committee\'s General Chairperson records it for him: ' + may.reason] };
  }
  if (!vice.designatedBy || vice.designatedBy === vice.uid) return { ok: false, errors: ['the designation of ' + vice.name + ' was not recorded by someone else - it cannot carry an installation (no self-grant)'] };
  const at = i.at || new Date().toISOString();
  const plan = planSetOfficeHolder({
    office, offices, terms,
    callerUid: vice.designatedBy,
    holder: { uid: vice.uid, email: vice.email, name: vice.name },
    holderIsAdmin: !!i.viceIsAdmin, holderOpenTerms: i.viceOpenTerms,
    startedOn: i.startedOn, method: 'interim',
    attestedBy: ['designated ' + (office.type === 'general_committee' ? 'Vice General Chairperson' : 'Vice Chairperson') + ' by ' + (vice.designatedByName || vice.designatedBy) + ' ' + String(vice.designatedOn || '').slice(0, 10), (callerIsVice ? vice.name + ' (vice) took up the office ' : (i.caller.name || i.caller.email || i.caller.uid) + ' recorded the step-up ') + at.slice(0, 10)],
    evidence: (office.type === 'general_committee' ? 'SMART Constitution SS82 lines 132-138' : 'SMART Constitution SS81 lines 32-40') + ' - the designated vice fills the vacancy in the interim (design S7)',
    notes: String(i.note || '').trim() || null, at,
  });
  if (!plan.ok) return plan;
  plan.newTerm.expectedEndOn = i.expectedEndOn;
  plan.officePatch = { status: 'held', statusReason: null, actingSince: i.startedOn };
  plan.audit = { ...plan.audit, action: 'step_up_interim', expectedEndOn: i.expectedEndOn, viceTookUp: callerIsVice };
  return plan;
}

// The flag. An interim term past its expected end is ACTING OVERDUE - reported wherever the office is
// named, never silently extended. Null when there is nothing to say.
export function interimFlag(term, today) {
  if (!term || term.method !== 'interim' || term.endedOn) return null;
  const t = day(today) || new Date().toISOString().slice(0, 10);
  if (!isDate(term.expectedEndOn)) return 'ACTING with no expected end on record - an interim term carries the date of the election that ends it';
  if (term.expectedEndOn < t) {
    const days = Math.round((Date.parse(t + 'T00:00:00Z') - Date.parse(term.expectedEndOn + 'T00:00:00Z')) / DAY_MS);
    return 'ACTING OVERDUE: the interim term was to end ' + term.expectedEndOn + ' (' + days + ' day' + (days === 1 ? '' : 's') + ' ago) and no election result has been seated. Temporary authority does not become permanent by silence.';
  }
  return null;
}
